<template>
  <div class="d-flex flex-column storage-search-input">
    <label
      :class="{
        'form-input-field': true,
        'form-input-field-label': true,
        'form-input-field-search-icon--active': isInputFocused,
        'form-input-field-search-icon--inactive': !isInputFocused
      }"
    >
      <v-icon class="form-input-field-search-icon" color="aab0bc"
        >mdi-magnify</v-icon
      >

      <div
        :class="{
          'd-flex flex-wrap': isRenderGrid()
        }"
      >
        <span
          v-for="selectedTag in selectedTags"
          :key="selectedTag.id"
          @click="removeSelectedTag(selectedTag)"
          class="d-flex secondary--text storage-search-input__selected-tag pointer"
          ><span class="d-flex align-center">
            <span class="text-uppercase font-weight-bold mr-1">{{
              `${$t("storage.tag")}:`
            }}</span
            >{{ selectedTag.tag }}</span
          >
        </span>
      </div>

      <input
        ref="formInputFieldRef"
        :value="storageSearchInputValue"
        @focus="handleFormInputFocus"
        @blur="handleFormInputBlur"
        @input="setStorageSearchInputValue($event.target.value)"
        @keydown="handleKeydownEvent"
        class="form-input-field__input"
        :placeholder="
          !isInputFocused ? $t('storage.search') : $t('storage.min_3_chars')
        "
        type="text"
        :disabled="disabled"
      />
      <v-icon v-show="isInputFocused" @click="clearInput">mdi-close</v-icon>
    </label>

    <StorageSearchCustomSelect
      v-show="isRenderSearchSelect"
      :fileName="fileName"
      :selectedTags="selectedTags"
      :toggleSelectVisible="toggleSelectVisible"
      :handleSelectedTags="handleSelectedTagsChange"
      :handleChangeFileName="handleFileNameChange"
      :handleSearchingInfoShow="handleSearchingInfoShow"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import { ModuleTypes } from "@/store/ModuleTypes";
import { debounce } from "lodash";
import StorageSearchCustomSelect from "@/views/academy/storage/files/StorageSearchCustomSelect";
import { KeyCodes } from "@/views/academy/enums/KeyCodes";
import { ACADEMY_PREFIX, Routes } from "@/router/Routes";

export default {
  name: "StorageSearchInput",
  components: {
    StorageSearchCustomSelect
  },
  props: {
    isAssignmentInput: {
      type: Boolean,
      default: false
    },
    handleSetAssignments: {
      type: Function,
      default: null
    },
    handleSearchingInfoShow: {
      type: Function,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fileName: {
      type: String,
      default: ""
    },
    handleFileNameChange: {
      type: Function,
      default: null
    },
    selectedTags: {
      type: Array,
      default: null
    },
    handleSelectedTagsChange: {
      type: Function,
      default: null
    },
    currentStatus: {
      type: String,
      default: ""
    }
  },
  data: () => ({
    isSelectVisible: true
  }),
  computed: {
    ...mapGetters(ModuleTypes.STORAGE, [
      "storageSearchInputValue",
      "activePlayer",
      "isInputFocused",
      "storageContent",
      "isCallSearchRequest"
    ]),

    isRenderSearchSelect() {
      return (
        this.$route.path.includes(`${ACADEMY_PREFIX}/storage`) &&
        !this.$route.params?.playerId &&
        this.storageSearchInputValue?.trim()?.length >= 3 &&
        this.isSelectVisible
      );
    }
  },

  methods: {
    ...mapMutations(ModuleTypes.STORAGE, [
      "setStorageSearchInputValue",
      "setStorageSearchInputFocused"
    ]),
    ...mapActions(ModuleTypes.STORAGE, ["fetchStorageContent"]),
    ...mapActions(ModuleTypes.ASSIGNMENTS, ["getAssignments"]),

    handleFormInputFocus() {
      this.setStorageSearchInputFocused(true);
      this.isSelectVisible = true;
    },
    handleFormInputBlur() {
      if (!this.storageSearchInputValue && !this.selectedTags?.length) {
        this.setStorageSearchInputFocused(false);
      }
    },
    clearInput() {
      this.setStorageSearchInputValue("");
      this.setStorageSearchInputFocused(false);
      if (
        this.$route.path?.includes(`${ACADEMY_PREFIX}/storage`) &&
        !this.$route.params?.playerId
      ) {
        this.handleSearchingInfoShow && this.handleSearchingInfoShow(false);
        this.handleSelectedTagsChange && this.handleSelectedTagsChange([]);
        this.handleFileNameChange && this.handleFileNameChange("");
      }
    },

    toggleSelectVisible(value) {
      this.isSelectVisible = value;
    },

    isRenderGrid() {
      return this.selectedTags?.length >= 3;
    },

    async removeSelectedTag(selectedTag) {
      const tags = this.selectedTags?.filter(
        item => item?.id !== selectedTag?.id
      );
      (await this.handleSelectedTagsChange) &&
        this.handleSelectedTagsChange(tags);

      const breadcrumbs = this.storageContent?.breadcrumbs;
      this.fetchStorageContent({
        organizationId:
          this.$route.params?.organizationId || this.$route.params?.orgId,
        parentId: breadcrumbs?.[breadcrumbs?.length - 1]?.id,
        playerId: null,
        searchValue: this.storageSearchInputValue,
        tags
      });
      if (this.selectedTags?.length === 0) {
        this.handleSearchingInfoShow && this.handleSearchingInfoShow(false);
      }
    },

    handleKeydownEvent(e) {
      if (!e.target.value?.length && e.keyCode === KeyCodes.BACKSPACE) {
        this.handleSelectedTagsChange &&
          this.handleSelectedTagsChange(this.selectedTags?.slice(0, -1));
        const selectedTagsIds = this.selectedTags?.map(item => item?.id);

        const breadcrumbs = this.storageContent?.breadcrumbs;
        this.fetchStorageContent({
          organizationId:
            this.$route.params?.organizationId || this.$route.params?.orgId,
          parentId: breadcrumbs?.[breadcrumbs?.length - 1]?.id,
          playerId: null,
          searchValue: null,
          tags: selectedTagsIds?.length ? selectedTagsIds : null
        });
      }
    }
  },

  watch: {
    storageSearchInputValue: debounce(function(newValue, oldValue) {
      if (!this.isCallSearchRequest) return;
      let searchValue = null;
      if (this.storageSearchInputValue.length >= 3) {
        searchValue = this.storageSearchInputValue;
      } else if (
        (oldValue.length === 3 && newValue.length === 2) ||
        (oldValue.length > 0 && newValue.length === 0)
      ) {
        searchValue = null;
      } else {
        return;
      }
      if (!this.$props.isAssignmentInput && this.$route.params.playerId) {
        const breadcrumbs = this.storageContent?.breadcrumbs;
        this.fetchStorageContent({
          organizationId:
            this.$route.params?.organizationId || this.$route.params?.orgId,
          parentId: breadcrumbs?.[breadcrumbs?.length - 1]?.id,
          playerId: this.activePlayer?.playerId,
          searchValue
        });
      } else {
        this.getAssignments({
          organizationId:
            this.$route.params?.organizationId || this.$route.params?.orgId,
          playerId: this.$route.params?.playerId,
          filter: searchValue,
          status: !newValue?.length ? this.currentStatus : null,
          callback: data => {
            if (this.handleSetAssignments) {
              this.handleSetAssignments({
                assignments: data,
                isSearchContextAssignments: searchValue
              });
            }
          }
        });
      }
    }, 500)
  }
};
</script>

<style scoped>
.storage-search-input {
  min-width: 230px;
  max-width: 400px;
}
.form-input-field-label {
  position: relative;
  display: block;
  padding: 0 8px;
}

.form-input-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--white-color);
  border-radius: 20px;
  outline: none;
}

.form-input-field__input {
  flex-grow: 1;
  padding: 8px 0;
  margin-left: 8px;
  outline: none;
}

.form-input-field:focus {
  box-shadow: 0 0 0 1px var(--grey-color);
}

.form-input-field-search-icon--inactive {
  background-color: var(--primary-color--dark);
}

.form-input-field-search-icon--active {
  background-color: var(--white-color);
}

.form-input-field-search-icon--active::placeholder {
  text-align: left;
}

.storage-search-input__selected-tag {
  padding: 2px 8px;
  margin: 4px;
  font-size: 12px;
  background-color: #e0e0e0;
  border-radius: 20px;
}

.pointer {
  cursor: pointer;
}
@media (max-width: 676px) {
  .storage-search-input {
    min-width: 100%;
  }
}
</style>
